/**
 * 할로윈 2026 웹사이트 이벤트 — 기간 판정 단일 출처 (#6061).
 *
 * 로고 · 커서 · 홈 오버레이 · 팝업 · 띠배너가 **모두 이 파일의 판정 하나**를 본다. 표면마다 시각을
 * 비교하면 「로고는 바뀌었는데 띠배너는 아직」 같은 어긋남이 난다(`promo-banner.ts` 가 GTA 때 막은 클래스).
 *
 * 디자인 정본: Figma `[디자인] Website` › 21. Event Pages › Halloween 2026 (NOTE `31438:7650`).
 *
 * ## 기간 — PT(`America/Los_Angeles`) 10/26 00:00 ~ 11/01 23:59
 *
 * 기획 확인 대기: 할로윈 **할인**의 정확한 시작·종료 시각과 시간대 — 장식은 할인 기간에 맞춘다(#6061).
 * 바뀌면 아래 두 상수만 고친다.
 *
 * ### ⚠️ 서머타임 — 시작과 끝의 오프셋이 다르다
 *
 * 2026 미국 서머타임이 **11/1(일) 02:00 에 끝난다**(기간 마지막 날). 그래서 시작은 PDT(UTC−7), 끝은
 * PST(UTC−8)다. 실측(Node `Intl`, 2026-10-22):
 *
 * ```
 *   시작 2026-10-26T00:00:00-07:00  = Oct 26 12:00 AM PDT = 한국 10/26 16:00
 *   종료 2026-11-01T23:59:59.999-08:00 = Nov 1 11:59 PM PST  = 한국 11/2 16:59
 *   (종료를 -07:00 으로 적으면 Nov 1 10:59 PM PST — 한 시간 일찍 끝난다)
 * ```
 *
 * GTA V 이벤트(`gta5-giveaway.ts`)가 같은 함정을 주석으로 남겼다 — 날짜를 옮기면 오프셋도 다시 본다.
 */
export const HALLOWEEN_2026_START_AT = new Date("2026-10-26T00:00:00-07:00");
export const HALLOWEEN_2026_END_AT = new Date("2026-11-01T23:59:59.999-08:00");

/** 표시 · 날짜 포맷에 쓰는 시간대 — 기간 상수와 같은 기준이어야 화면 날짜가 판정과 맞는다. */
export const HALLOWEEN_2026_TIME_ZONE = "America/Los_Angeles";

/**
 * 기간 안인가 — 시작 이상, 종료 이하.
 *
 * ⚠️ `now` 를 인자로 받는다. 안에서 `new Date()` 를 부르면 테스트가 시스템 시계에 묶인다.
 */
export function isWithinHalloween2026(now: Date): boolean {
  const t = now.getTime();
  return (
    t >= HALLOWEEN_2026_START_AT.getTime() &&
    t <= HALLOWEEN_2026_END_AT.getTime()
  );
}

/**
 * ★서버가 내려준 「기간 안인가」를 담는 전역 키 — `_document` 가 서버 시계로 세우고 클라가 읽는다.
 *
 * 띠배너(`PROMO_WINDOW_GLOBAL`, #5702)와 같은 구조다. 클라가 자기 시계로 판정하면 시계가 틀어진 기기에서
 * SSR(서버 시계)과 결론이 갈려 **hydration 이 어긋난다** — 로고는 모든 페이지에 있어 그 깜빡임이 매번 보인다.
 *
 * ⚠️ **「서버 시계」는 요청 렌더(GSSP) 페이지에서만 요청 시각이다** — 정적 · SSG 페이지(404 · 블로그 목록 · 약관 등)는
 * 빌드 때 HTML 이 만들어져 이 전역이 **빌드 시각으로 굳는다**. 기간 중 배포 후 재배포가 없으면 이벤트가 끝나도 장식이
 * 남는다(#6066 R1). 그래서 `useHalloween2026` 은 그런 페이지에서 이 값을 hydration 에만 쓰고 그 뒤 기기 시계로
 * 다시 판정한다. 다음 이벤트가 이 파일을 복사하면 그 훅의 처리도 같이 가져간다.
 */
export const HALLOWEEN_2026_WINDOW_GLOBAL = "__BH_HALLOWEEN_2026__";

/**
 * 미리보기 스위치 — **비-prod(local · dev)는 기간 안이면 기본으로 켜져 있다.** `?halloween-preview=0` 이면
 * 그 탭에서 끄고, `=1` 이면 그 페이지는 **기간 밖이어도** 켠다(이벤트가 끝난 뒤 시안 대조용 — 쿼리를 붙인
 * 페이지에서만). prod·staging(`NEXT_PUBLIC_ENV=production`)에서는 무시한다.
 *
 * 처음엔 쿼리를 붙였을 때만 켰지만(사이트 전역 장식이라 다른 검수를 가린다는 이유), 확인할 때마다 붙여야 해서
 * 불편하다는 결정으로 GTA(당시 `isGta5GiveawayOpenWith` — 비-prod 항상 켬, #6149 에서 걷음)와 같은 기본값으로 돌렸다(#6061, 2026-10-22).
 * 그 기본값이 **기간과 무관**이라 이벤트가 끝난(2026-11-02 KST 17:00) 뒤에도 dev 에 장식이 남았다 — 기간 밖
 * 기본값은 prod 와 같은 「끔」으로 맞췄다.
 */
export const HALLOWEEN_2026_PREVIEW_PARAM = "halloween-preview";

/** 비-prod 에서 「이 탭은 끔」을 기억하는 sessionStorage 키 — 사이트 안 이동에도 유지된다. */
export const HALLOWEEN_2026_PREVIEW_OFF_STORAGE_KEY =
  "bh-halloween-2026-preview-off";

/**
 * 할로윈 장식을 켤까 — 우선순위는 여기 한 곳에만 둔다.
 *
 * ```
 *   prod(+staging)   기간 안일 때만(서버 시계) — 미리보기 무시
 *   비-prod          `=1` 이면 켬(기간 무관) · 아니면 기간 안이고 미리보기를 안 끈 탭만 켬
 * ```
 */
export function isHalloween2026Active({
  isProd,
  withinWindow,
  preview,
  forced,
}: {
  isProd: boolean;
  withinWindow: boolean;
  /** 비-prod 에서 이 탭의 미리보기를 끄지 않았나(`=0` · 끈 기록 없음). */
  preview: boolean;
  /** 비-prod 에서 이 페이지에 `?halloween-preview=1` 을 붙였나 — 기간 밖에서도 켠다. */
  forced: boolean;
}): boolean {
  if (isProd) return withinWindow;
  return forced || (withinWindow && preview);
}

/**
 * 팝업 · 띠배너 **문구가 들어왔나** — 기획 전달 완료(#6061, 2026-10-22 · 영어 원문, 6개 언어는 개발 번역).
 *
 * ```
 *   false   prod   팝업은 안 띄우고, 띠배너는 이름 · 기간만(모바일 시안과 같은 꼴)
 *           비-prod 팝업도 띄운다(문구 자리 빈 채) — 시안 대조용
 *   true    문구 · CTA 를 그린다 — 번역 키(`gnb`)와 아래 링크가 함께 들어와야 한다
 * ```
 *
 * 문구 키는 둘이다 — 팝업 본문과 띠 메시지가 같은 문장(`halloween2026Message`), 버튼과 띠 CTA 가 같은 라벨(`halloween2026Cta`).
 * 팝업 보조 문구는 기간이라 `halloween2026Period` 를 같이 쓴다. 가드가 「true 인데 키 · 링크가 비었음」을 잡는다.
 */
export const HALLOWEEN_2026_COPY_READY = true;

/** 띠배너 CTA 목적지 — 기획 전달(2026-10-22): TactSuit 목록. */
export const HALLOWEEN_2026_STRIP_CTA_HREF: string | null = "/tactsuit";

/** 팝업 버튼 목적지 — 기획 전달(2026-10-22): TactSuit 목록. */
export const HALLOWEEN_2026_POPUP_CTA_HREF: string | null = "/tactsuit";
